/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, HFlow, Icon, Tooltip, VFlow } from 'bold-ui'
import { Form, FormDebouncedValueSpy, SwitchField, TextField } from 'components/form'
import { PopperButton, PopperControls } from 'components/popper'
import theme from 'config/theme'
import { FormState } from 'final-form'
import { CuidadoCompartilhadoQueryFilterEnum } from 'graphql/types.generated'
import { useFilterCounter } from 'hooks/useFilterCounter'
import { Fragment } from 'react'
import { isUndefinedOrNull } from 'util/checks'
import { metaPath } from 'util/metaPath'
import {
  CuidadoCompartilhadoSortEnum,
  ListaCuidadoCompartilhadoFilterModel,
  ListaCuidadoCompartilhadoInlineFilterModel,
} from 'view/cuidados-compartilhados/model-cuidadoCompartilhado'

import { ListaCuidadoCompartilhadoFilterPopperForm } from './ListaCuidadoCompartilhadoFilterPopperForm'
import { ListaCuidadoCompartilhadoFilterTags } from './ListaCuidadoCompartilhadoFilterTags'
import { ListaCuidadoCompartilhadoSortDropdown } from './ListaCuidadoCompartilhadoSortDropdown'

interface ListaCuidadoCompartilhadoFilterProps {
  value: ListaCuidadoCompartilhadoFilterModel
  itemsToSort: CuidadoCompartilhadoSortEnum[]
  filterSwitchLabel: string
  isFilterDefault: boolean
  role: CuidadoCompartilhadoQueryFilterEnum
  isEstabelecimentoEspecializado: boolean
  onChange(value: ListaCuidadoCompartilhadoFilterModel): void
  onClear: () => void
  somenteSemExecutante?: boolean
}

const meta = metaPath<ListaCuidadoCompartilhadoInlineFilterModel>()

const mountInitialValues = (values: ListaCuidadoCompartilhadoFilterModel): ListaCuidadoCompartilhadoFilterModel => {
  const periodo = values.periodo

  if (isUndefinedOrNull(periodo)) return { ...values }

  const unmountedStartDate = periodo.startDate
  const unmountedEndDate = periodo.endDate

  const startDate = isUndefinedOrNull(unmountedStartDate) ? null : new Date(unmountedStartDate)
  const endDate = isUndefinedOrNull(unmountedEndDate) ? null : new Date(unmountedEndDate)

  return {
    ...values,
    periodo: {
      startDate,
      endDate,
    },
  }
}

export const ListaCuidadoCompartilhadoFilter = (props: ListaCuidadoCompartilhadoFilterProps) => {
  const {
    value,
    itemsToSort,
    filterSwitchLabel,
    isEstabelecimentoEspecializado,
    isFilterDefault,
    role,
    onChange,
    onClear,
    somenteSemExecutante,
  } = props

  const mountedValues = mountInitialValues(value)

  const isCompartilhadosComUnidade = isEstabelecimentoEspecializado && somenteSemExecutante === true

  const handleDebounceChange = (formState: FormState<ListaCuidadoCompartilhadoFilterModel>) => {
    const newProfSolicitante =
      formState.values.switch && role === CuidadoCompartilhadoQueryFilterEnum.SOLICITANTE
        ? null
        : value.profissionalSolicitante
    const newProfExecutante =
      formState.values.switch && role === CuidadoCompartilhadoQueryFilterEnum.EXECUTANTE
        ? null
        : value.profissionalExecutante

    onChange({
      ...value,
      ...formState.values,
      query: formState.values.query,
      profissionalSolicitante: newProfSolicitante,
      profissionalExecutante: newProfExecutante,
    })
  }

  const renderPopper = (ctrl: PopperControls) => (
    <ListaCuidadoCompartilhadoFilterPopperForm
      isFilterDefault={isFilterDefault}
      isEstabelecimentoEspecializado={isEstabelecimentoEspecializado}
      initialValues={mountedValues}
      role={role}
      somenteSemExecutante={somenteSemExecutante}
      onSubmit={onChange}
      onClear={onClear}
      onClose={ctrl.close}
    />
  )

  const countFiltrosAplicados = useFilterCounter(value, {
    query: false,
    switch: false,
    switchNotificacao: false,
    pageParams: false,
    lastUpdate: false,
  })

  const renderInlineFilterForm = () => {
    return (
      <Fragment>
        <FormDebouncedValueSpy onChange={handleDebounceChange} />
        <HFlow justifyContent='space-between' alignItems='center' style={styles.container}>
          <HFlow alignItems='center' style={styles.leftGroup}>
            <div css={styles.searchWrapper}>
              <TextField
                name={meta.query}
                placeholder='Pesquise por nome, CPF, CNS do cidadão'
                icon='zoomOutline'
                style={styles.query}
              />
            </div>

            {!isUndefinedOrNull(filterSwitchLabel) && (
              <div css={styles.switchWrapper}>
                <SwitchField name={meta.switch} label={filterSwitchLabel} />
              </div>
            )}

            {role !== CuidadoCompartilhadoQueryFilterEnum.GERENTE && (
              <div css={styles.switchWrapper}>
                <SwitchField
                  name={meta.switchNotificacao}
                  label='Ver somente registros com notificação'
                  style={styles.switchNotificacao}
                />
              </div>
            )}
          </HFlow>

          <HFlow alignItems='center' style={styles.rightGroup}>
            {!isCompartilhadosComUnidade && (
              <ListaCuidadoCompartilhadoSortDropdown items={itemsToSort} filter={value} onChange={onChange} />
            )}

            <PopperButton
              kind='primary'
              size='small'
              skin='default'
              placement='bottom-end'
              renderPopper={renderPopper}
              style={styles.filtroPopper}
            >
              <HFlow hSpacing={0.5}>
                <Icon icon='filterOutline' />
                {'Filtros' + (countFiltrosAplicados > 0 ? ` (${countFiltrosAplicados})` : '')}
              </HFlow>
            </PopperButton>
          </HFlow>
        </HFlow>
      </Fragment>
    )
  }

  return (
    <VFlow vSpacing={0.5}>
      <Form<ListaCuidadoCompartilhadoInlineFilterModel>
        render={renderInlineFilterForm}
        initialValues={{
          switch: value.switch,
          query: value.query,
          switchNotificacao: value.switchNotificacao,
        }}
      />
      <HFlow alignItems='center' justifyContent='space-between' style={{ gridAutoColumns: 'minmax(0, auto)' }}>
        <ListaCuidadoCompartilhadoFilterTags filters={mountedValues} />

        <Tooltip text='Redefinir filtros para o padrão'>
          <Button kind='primary' skin='outline' size='small' disabled={isFilterDefault} onClick={onClear}>
            <Icon style={styles.redoIcon} icon='redo' />
            Voltar para padrão
          </Button>
        </Tooltip>
      </HFlow>
    </VFlow>
  )
}

const styles = {
  container: css`
    display: flex;
    flex-wrap: nowrap;
  `,
  leftGroup: css`
    display: flex;
    flex: 1 1 auto;
    min-width: 0;

    ${theme.breakpoints.down('lg')} {
      gap: 0.5rem;
    }
  `,
  rightGroup: css`
    display: flex;
    flex: 0 1 auto;

    ${theme.breakpoints.down('lg')} {
      gap: 0.5rem;
    }
  `,
  searchWrapper: css`
    flex: 1 1 10rem;
    min-width: 10rem;
    max-width: 20rem;
  `,
  query: css`
    width: 100%;
    text-overflow: ellipsis;
  `,
  switchWrapper: css`
    flex: 0 1 auto;
    max-width: 22rem;

    & label span {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    ${theme.breakpoints.down('md')} {
      & label span {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  `,
  switchNotificacao: css`
    margin-left: 1rem;

    ${theme.breakpoints.down('lg')} {
      margin-left: 0.5rem;
    }
  `,
  filtroPopper: css`
    white-space: nowrap;
  `,
  redoIcon: css`
    padding-right: 0.5rem;
  `,
}
